import { useEffect, useRef, useState } from 'react'
import { Avatar } from '@mui/material'
import SmartToy from '@mui/icons-material/SmartToy'

import Markdown from '../../study/components/Markdown'
import defaultAvatar from '../../icon/defaultAvatars/user.png'
import { getReasoningGapChat, sendReasoningGapChat } from '../../service/reasoningGapService'
import '../workspace/css/workspace.scss'

// In-task AI assistant panel for Reasoning Gap sessions. Built on the classroom workspace's
// chat styles (ws-chat) so it looks like the other VizPI session types. Every question is
// sent with the student's CURRENT code and explanation; the server stores both with the
// message. The assistant closes once the student submits.

const CHARACTER_LIMIT = 2000

const formatTime = value => new Date(value).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })

export default function ReasoningGapChat ({ sessionId, userId, getCode, getExplanation, log }) {
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [isThinking, setIsThinking] = useState(false)
  const [error, setError] = useState('')
  const historyRef = useRef(null)

  useEffect(() => {
    if (!sessionId || !userId) return undefined
    let cancelled = false
    getReasoningGapChat(sessionId, userId)
      .then(data => !cancelled && setMessages(data.messages || []))
      .catch(() => !cancelled && setError('Could not load your earlier conversation.'))
    return () => { cancelled = true }
  }, [sessionId, userId])

  useEffect(() => {
    if (historyRef.current) historyRef.current.scrollTop = historyRef.current.scrollHeight
  }, [messages, isThinking])

  const send = async () => {
    const message = input.trim()
    if (!message || isThinking || message.length > CHARACTER_LIMIT) return
    const code = getCode()
    const explanation = getExplanation()
    setInput('')
    setError('')
    setIsThinking(true)
    // Show the question right away; the server's copy replaces it when the reply arrives.
    const pending = { id: `pending-${Date.now()}`, role: 'user', content: message, created_at: new Date().toISOString() }
    setMessages(current => [...current, pending])
    log('CHAT_QUERY', { message, codeLength: code.length, explanationLength: explanation.length })
    const sentAt = Date.now()
    try {
      const pair = await sendReasoningGapChat(sessionId, { userId, message, code, explanation })
      setMessages(current => [...current.filter(m => m.id !== pending.id), ...pair])
      log('CHAT_RESPONSE', { length: pair[1]?.content?.length || 0, latencyMs: Date.now() - sentAt })
    } catch (e) {
      const saved = e.response?.data?.messages
      setMessages(current => [...current.filter(m => m.id !== pending.id), ...(saved || [pending])])
      setError(e.response?.data?.error || 'The assistant is unavailable right now. Try again in a moment.')
      log('CHAT_ERROR', { status: e.response?.status || null })
    } finally {
      setIsThinking(false)
    }
  }

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      send()
    }
  }

  const onCopy = () => {
    const text = String(window.getSelection ? window.getSelection() : '')
    if (text) log('CHAT_COPY_HISTORY', { length: text.length, text: text.slice(0, 2000) })
  }

  return (
    <div className="ws-chat-container rg-chat">
      <div className="ws-chat">
        <div className="rg-chat__head">
          <span className="ws-section-title">AI Assistant</span>
          <span className="rg-muted">Sees your current code and explanation. It won't write the solution for you.</span>
        </div>

        <div className="ws-chat-history" ref={historyRef} onCopy={onCopy}>
          {messages.length === 0 && !isThinking && (
            <div className="rg-chat__empty">
              Ask about the task, an error message, a Python function, or whether your approach makes sense.
            </div>
          )}
          {messages.map(m => {
            const isAI = m.role === 'assistant'
            return (
              <div key={m.id} className={`ws-message ${isAI ? '' : 'ws-owner'}`}>
                <div className="ws-message-info">
                  {isAI ? (
                    <Avatar sx={{ bgcolor: '#1976d2', width: 30, height: 30 }}><SmartToy sx={{ fontSize: '1rem' }} /></Avatar>
                  ) : (
                    <img src={defaultAvatar} alt="" />
                  )}
                  <span className="ws-info-name">{isAI ? 'AI Assistant' : 'You'}</span>
                  <span className="ws-info-time">{formatTime(m.created_at)}</span>
                </div>
                <div className="ws-message-content-wrapper">
                  <div className="ws-message-content">
                    {isAI
                      ? <div className="rg-chat__reply"><Markdown text={m.content} /></div>
                      : <pre>{m.content}</pre>}
                  </div>
                </div>
              </div>
            )
          })}
          {isThinking && (
            <div className="ws-message">
              <div className="ws-message-info">
                <Avatar sx={{ bgcolor: '#1976d2', width: 30, height: 30 }}><SmartToy sx={{ fontSize: '1rem' }} /></Avatar>
                <span className="ws-info-name">AI Assistant</span>
              </div>
              <div className="ws-message-content-wrapper">
                <div className="ws-message-content"><pre className="rg-chat__thinking">Thinking...</pre></div>
              </div>
            </div>
          )}
        </div>

        {error && <div className="rg-chat__error">{error}</div>}

        <form className="ws-message-form" onSubmit={(e) => { e.preventDefault(); send() }}>
          <textarea
            id="rg-chat-input"
            className="ws-message-input"
            placeholder={isThinking ? 'The assistant is replying...' : 'Ask a question (Enter to send, Shift+Enter for a new line)'}
            rows={1}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            onPaste={e => log('CHAT_PASTE', { length: e.clipboardData.getData('text').length, text: e.clipboardData.getData('text').slice(0, 2000) })}
            disabled={isThinking}
          />
          <div className="ws-char-count">
            <span style={{ color: input.length > CHARACTER_LIMIT ? '#d32f2f' : '#999' }}>{input.length}/{CHARACTER_LIMIT}</span>
          </div>
          <button
            type="submit"
            className="ws-send-button"
            disabled={isThinking || !input.trim() || input.length > CHARACTER_LIMIT}
          >
            Send
          </button>
        </form>
      </div>
    </div>
  )
}
